import { maruyaApi } from './maruya-api'
import type { RagAskResponse } from '../types/api'

export type ChatMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string
  response?: RagAskResponse
  createdAt: string
}

export type ChatThread = {
  id: string
  title: string
  messages: ChatMessage[]
  createdAt: string
  updatedAt: string
}

type ChatHistorySnapshot = {
  version: 1
  threads: ChatThread[]
  activeThreadId: string | null
}

const STORAGE_VERSION = 1
const MAX_THREADS = 50

function storageKey(userId: string) {
  return `maruya-rag-chat-history:${userId}`
}

// CX7: 履歴のサーバー同期。設計: docs/maruya/maruya-chat-ux-cx6-multiturn-design.md §8.5。

function migratedFlagKey(userId: string) {
  return `maruya-rag-chat-history-migrated:${userId}`
}

function serverIdMapKey(userId: string) {
  return `maruya-rag-chat-history-server-map:${userId}`
}

type ServerChatMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string
  response?: RagAskResponse
  created_at: string
}

type ServerChatThread = {
  id: string
  title: string
  created_at: string
  updated_at: string
  messages: ServerChatMessage[]
}

function truncateTitle(text: string, maxLength = 48) {
  const normalized = text.trim().replace(/\s+/g, ' ')
  if (normalized.length <= maxLength) {
    return normalized
  }
  return `${normalized.slice(0, maxLength)}…`
}

export function createMessage(
  role: ChatMessage['role'],
  content: string,
  response?: RagAskResponse,
): ChatMessage {
  return {
    id: crypto.randomUUID(),
    role,
    content,
    response,
    createdAt: new Date().toISOString(),
  }
}

export function createThread(firstQuestion?: string): ChatThread {
  const now = new Date().toISOString()
  return {
    id: crypto.randomUUID(),
    title: firstQuestion ? truncateTitle(firstQuestion) : '新しい会話',
    messages: [],
    createdAt: now,
    updatedAt: now,
  }
}

export function loadChatHistory(userId: string): ChatHistorySnapshot {
  try {
    const raw = localStorage.getItem(storageKey(userId))
    if (!raw) {
      return { version: STORAGE_VERSION, threads: [], activeThreadId: null }
    }

    const parsed = JSON.parse(raw) as ChatHistorySnapshot
    if (parsed.version !== STORAGE_VERSION || !Array.isArray(parsed.threads)) {
      return { version: STORAGE_VERSION, threads: [], activeThreadId: null }
    }

    return {
      version: STORAGE_VERSION,
      threads: parsed.threads,
      activeThreadId: parsed.activeThreadId ?? null,
    }
  } catch {
    return { version: STORAGE_VERSION, threads: [], activeThreadId: null }
  }
}

export function saveChatHistory(userId: string, snapshot: ChatHistorySnapshot) {
  const threads = [...snapshot.threads]
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, MAX_THREADS)

  const activeThreadId =
    snapshot.activeThreadId &&
    threads.some((thread) => thread.id === snapshot.activeThreadId)
      ? snapshot.activeThreadId
      : (threads[0]?.id ?? null)

  localStorage.setItem(
    storageKey(userId),
    JSON.stringify({
      version: STORAGE_VERSION,
      threads,
      activeThreadId,
    } satisfies ChatHistorySnapshot),
  )

  return { threads, activeThreadId }
}

export function upsertThreadMessage(
  threads: ChatThread[],
  threadId: string,
  message: ChatMessage,
  title?: string,
): ChatThread[] {
  return threads.map((thread) => {
    if (thread.id !== threadId) {
      return thread
    }

    const nextTitle =
      thread.messages.length === 0 && message.role === 'user' && title
        ? truncateTitle(title)
        : thread.title

    return {
      ...thread,
      title: nextTitle,
      messages: [...thread.messages, message],
      updatedAt: message.createdAt,
    }
  })
}

export function renameThread(
  threads: ChatThread[],
  threadId: string,
  rawTitle: string,
): ChatThread[] {
  const trimmed = rawTitle.trim()
  if (!trimmed) {
    return threads
  }

  return threads.map((thread) =>
    thread.id === threadId ? { ...thread, title: truncateTitle(rawTitle) } : thread,
  )
}

// CX16: 「編集して再送信」。分岐履歴は作らず、対象メッセージ以降を丸ごと削除してから編集後の質問を末尾に追記する「置き換え」方式。
export function truncateThreadFrom(
  threads: ChatThread[],
  threadId: string,
  messageId: string,
): ChatThread[] {
  return threads.map((thread) => {
    if (thread.id !== threadId) {
      return thread
    }

    const index = thread.messages.findIndex((message) => message.id === messageId)
    if (index === -1) {
      return thread
    }

    return {
      ...thread,
      messages: thread.messages.slice(0, index),
    }
  })
}

export function filterThreads(threads: ChatThread[], query: string): ChatThread[] {
  const normalized = query.trim().toLowerCase()
  if (!normalized) {
    return threads
  }

  return threads.filter((thread) => {
    if (thread.title.toLowerCase().includes(normalized)) {
      return true
    }
    return thread.messages.some((message) => message.content.toLowerCase().includes(normalized))
  })
}

export function formatThreadDate(iso: string) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return ''
  }

  return date.toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// --- CX7: 履歴のサーバー同期。設計: docs/maruya/maruya-chat-ux-cx6-multiturn-design.md §8.5。---
// ローカル操作は即時反映（本ファイルの上記関数群）を維持したまま、以下はサーバーとの非同期同期を担う。
// 失敗してもローカル操作は失敗させない（fire-and-forget、次回操作時に再試行）。

function serverThreadToLocal(dto: ServerChatThread): ChatThread {
  return {
    id: dto.id,
    title: dto.title,
    messages: dto.messages.map((m) => ({
      id: m.id,
      role: m.role,
      content: m.content,
      response: m.response,
      createdAt: m.created_at,
    })),
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
  }
}

function getServerIdMap(userId: string): Record<string, string> {
  try {
    const raw = localStorage.getItem(serverIdMapKey(userId))
    if (!raw) {
      return {}
    }
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function getServerThreadId(userId: string, localThreadId: string): string | undefined {
  return getServerIdMap(userId)[localThreadId]
}

function setServerThreadId(userId: string, localThreadId: string, serverThreadId: string) {
  const map = getServerIdMap(userId)
  map[localThreadId] = serverThreadId
  localStorage.setItem(serverIdMapKey(userId), JSON.stringify(map))
}

export function isHistoryMigrated(userId: string): boolean {
  return localStorage.getItem(migratedFlagKey(userId)) === '1'
}

function markHistoryMigrated(userId: string) {
  localStorage.setItem(migratedFlagKey(userId), '1')
}

async function fetchServerThreads(): Promise<ChatThread[]> {
  const data = await maruyaApi<{ threads: ServerChatThread[] }>('/v1/chat/threads')
  return (data.threads ?? []).map(serverThreadToLocal)
}

async function createServerThread(title: string): Promise<ChatThread> {
  const data = await maruyaApi<{ thread: ServerChatThread }>('/v1/chat/threads', {
    method: 'POST',
    body: { title },
  })
  return serverThreadToLocal(data.thread)
}

async function appendServerMessage(
  serverThreadId: string,
  message: ChatMessage,
): Promise<ChatThread> {
  const data = await maruyaApi<{ thread: ServerChatThread }>(
    `/v1/chat/threads/${encodeURIComponent(serverThreadId)}/messages`,
    {
      method: 'POST',
      body: { role: message.role, content: message.content, response: message.response },
    },
  )
  return serverThreadToLocal(data.thread)
}

/**
 * ローカルに即時反映済みのメッセージをサーバーへ非同期で同期する。
 * スレッドがまだサーバー未作成なら作成してから対応付け（localThreadId → serverThreadId）を保存する。
 * 失敗時は例外を投げず、対応付けも更新しない（＝次回のメッセージ送信時に再試行される）。
 */
export async function syncThreadToServer(
  userId: string,
  thread: ChatThread,
  message: ChatMessage,
): Promise<void> {
  try {
    let serverThreadId = getServerThreadId(userId, thread.id)
    if (!serverThreadId) {
      const created = await createServerThread(thread.title)
      serverThreadId = created.id
      setServerThreadId(userId, thread.id, serverThreadId)
    }
    await appendServerMessage(serverThreadId, message)
  } catch (err) {
    console.warn('[chat-history] syncThreadToServer failed, will retry on next operation', err)
  }
}

/**
 * 既存 localStorage の全スレッドをサーバーへ一括アップロードする（初回ログイン時の移行）。
 * スレッドごとに作成 → メッセージを createdAt 昇順で追加。部分失敗しても再送はせず、
 * 完了後に移行済みフラグを立てる（重複スレッド作成を避けるため）。
 */
export async function migrateLocalHistoryToServer(userId: string): Promise<void> {
  if (isHistoryMigrated(userId)) {
    return
  }

  const { threads } = loadChatHistory(userId)
  if (threads.length === 0) {
    markHistoryMigrated(userId)
    return
  }

  for (const thread of threads) {
    try {
      const created = await createServerThread(thread.title)
      setServerThreadId(userId, thread.id, created.id)

      const sortedMessages = [...thread.messages].sort((a, b) =>
        a.createdAt.localeCompare(b.createdAt),
      )
      for (const message of sortedMessages) {
        try {
          await appendServerMessage(created.id, message)
        } catch (err) {
          console.warn('[chat-history] migration: message upload failed', err)
        }
      }
    } catch (err) {
      console.warn('[chat-history] migration: thread upload failed', err)
    }
  }

  markHistoryMigrated(userId)
}

/**
 * サーバーのスレッド一覧を取得し、ローカル state をサーバー優先で置き換える。
 * ローカルにしか無い未同期の変更（オフライン編集）のマージは非対応（設計 §8.5 の既知の制約）。
 */
export async function syncChatHistoryFromServer(
  userId: string,
): Promise<{ threads: ChatThread[]; activeThreadId: string | null }> {
  const serverThreads = await fetchServerThreads()
  const current = loadChatHistory(userId)
  return saveChatHistory(userId, {
    version: STORAGE_VERSION,
    threads: serverThreads,
    activeThreadId: current.activeThreadId,
  })
}

/**
 * 初回ロード時のエントリポイント: 未移行なら移行してから、サーバー優先でローカル state を同期する。
 */
export async function initServerChatHistorySync(
  userId: string,
): Promise<{ threads: ChatThread[]; activeThreadId: string | null }> {
  await migrateLocalHistoryToServer(userId)
  return syncChatHistoryFromServer(userId)
}
